<script setup>
import { useStore } from 'vuex'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { KkTextarea, KkTextField, KkValidationError, KkLoading } from '../components/base'
import { useRoute, useRouter } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { KkInfo } from '../components/ui'
import { KkInsertParameter } from '../components/projects'

const store = useStore()
const { company_uuid: companyUuid, reply_template_id: replyTemplateId } = useRoute().params
const router = useRouter()
const { t } = useI18n()

const schema = {
  name: 'required|max:40',
  content: 'required|max:500'
}

const { handleSubmit, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    name: '',
    content: ''
  },
})

const templateContent = ref(null)

const replyTemplate = computed(() => store.state.companiesReplyTemplate.data)
const loading = computed(() => store.state.companiesReplyTemplate.loading)
const availableParameters = computed(() => store.getters['companiesParameters/availableParameters'])
const parameterLoading = computed(() => store.getters['companiesParameters/loading'])

const readReplyTemplate = () => store.dispatch('companiesReplyTemplate/readReplyTemplate', { reply_template_id: replyTemplateId })
const createReplyTemplate = ({ payload }) => store.dispatch('companiesReplyTemplate/createReplyTemplate',{ payload })
const updateReplyTemplate = ({ reply_template_id: replyTemplateId, payload }) => store.dispatch('companiesReplyTemplate/updateReplyTemplate', { reply_template_id: replyTemplateId, payload })
const readParameters = () => store.dispatch('companiesParameters/readParameters')

const { value: name, errorMessage: nameError, handleChange: nameHandleChange } = useField('name', undefined, { label: t('models.replyTemplate.replyTemplateName') })
const { value: content, errorMessage: contentError,  handleChange: contentHandleChange } = useField('content', undefined, { label: t('models.replyTemplate.replyTemplateContent') })

const initData = async () => {
  if (replyTemplateId) {
    await readReplyTemplate()
    setValues({
      name:replyTemplate.value.name,
      content: replyTemplate.value.content
    })
  }
  readParameters()
}
const handleConfirm = handleSubmit(async(value) => {
  const payload = {
    name: value.name,
    content: value.content
  }
  await submitApi(payload)
    .then(() => {
      router.push({ name: 'CompaniesManagementReplyTemplates', params: { company_uuid: companyUuid } })
    })
})
const submitApi = (payload) => {
  if (replyTemplateId) {
    return updateReplyTemplate({ reply_template_id: replyTemplateId, payload })
  } else {
    return createReplyTemplate({ payload })
  }
}
const insertParam = (parameterName) => {
  const pos = templateContent.value.$refs.textarea.selectionStart
  const before = content.value.substring(0, pos)
  const after = content.value.substring(pos, content.value.length)
  content.value = before + parameterName + after
}
initData()
</script>

<template>
  <div>
    <div class="p-box">
      <div class="p-box__body">
        <KkInfo>
          <p>{{ t('pages.companies.management.replyTemplates.inputInfo1') }}</p>
          <p>{{ t('pages.companies.management.replyTemplates.inputInfo2') }}</p>
        </kkInfo>
        <form v-if="!loading">
          <div class="field">
            <label class="c-formLabel">
              {{ t('models.replyTemplate.replyTemplateName') }}
              {{
                t('pages.companies.replyTemplateFrom.label.name', {count: name.length, max: 40})
              }}{{ t('labels.requiredLabel') }}
            </label>
            <KkTextField :model-value="name" class="c-textField" :placeholder="t('labels.placeholder.enter', { value: t('models.replyTemplate.replyTemplateName') })" @update:model-value="nameHandleChange" />
            <KkValidationError :text="nameError" />
          </div>
          <div class="field">
            <div class="formLabelWrapper">
              <label class="c-formLabel">{{ t('models.replyTemplate.replyTemplateContent') }}
                {{
                  t('pages.companies.replyTemplateFrom.label.content', {count: content.length, max: 500})
                }}{{ t('labels.requiredLabel') }}
              </label>
              <KkInsertParameter
                class="insertParameter"
                :parameters="availableParameters"
                :loading="parameterLoading"
                @insert-parameter="insertParam"
              />
            </div>
            <KkTextarea ref="templateContent" :model-value="content" class="c-textArea" height="200" :placeholder="t('labels.placeholder.enter', { value: t('models.replyTemplate.replyTemplateContent') })" @update:model-value="contentHandleChange" />
            <KkValidationError :text="contentError" />
          </div>
        </form>
      </div>
      <footer class="p-box__footer">
        <div class="c-button c-button--right">
          <router-link class="c-button__item c-button__item--outline-cancel" :to="{ name: 'CompaniesManagementReplyTemplates', params: { company_uuid: companyUuid } }">{{ t('labels.cancel') }}</router-link>
          <button class="c-button__item c-button__item--primary" @click="handleConfirm">
            {{ t('labels.confirm') }}
          </button>
        </div>
      </footer>
      <KkLoading v-if="loading" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../stylesheets/visitor/foundation/_var.scss';
.field {
  width: 50%;
  margin-top: 33px;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
}
.formLabelWrapper {
  display: flex;
  justify-content: space-between;
}
.insertParameter {
  margin-bottom: 8px;
}
</style>
